import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import StandardModal from 'modals/StandardModal';
import Spinner from 'components/Spinner';
import { useDatasetMonitor } from 'data/repositories/datasetMonitorRepository';
import { closeModal } from 'data/reducers/modalReducer';
import DatasetStatCards from './DatasetStatCards';
import SendsTable from './SendsTable';

const SENDS_PER_PAGE = 10;
const FIRST_PAGE = 1;

/**
 * DatasetDrawer
 *
 * The per-dataset drilldown (design spec 10 `show` / `sends`), registered
 * through ModalManager following the PropertyStatsModal precedent: the
 * stat-card overview (DatasetStatCards, all four email types in full) plus
 * the paginated sends list (SendsTable).
 */
const DatasetDrawer = ({ options }) => {
	const dispatch = useDispatch();
	const { datasetKey, hours } = options || {};
	const {
		actions,
		dataset,
		isLoadingDataset,
		sends,
		sendsPagination,
		isLoadingSends,
	} = useDatasetMonitor();

	const [sendsPage, setSendsPage] = useState(FIRST_PAGE);

	// Dataset drilldown: fetched whenever a different row is opened OR the
	// container's reporting window changes while this row's drawer is
	// already open (review finding: this used to fetch an independent,
	// window-less request, so it kept reading "Coverage 24h" even after the
	// table moved to a different window). `hours` flows in from the
	// container via modalOptions - see DatasetMonitor.jsx.
	useEffect(() => {
		if (!datasetKey) {
			return;
		}
		actions.getDataset({ key: datasetKey, hours });
		// datasetKey/hours are the only things that should re-trigger this;
		// actions is a stable identity from useDatasetMonitor.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [datasetKey, hours]);

	// Sends list: reset to page 1 and fetch, but ONLY when the dataset
	// changes - never when `hours` changes, since /sends is not windowed by
	// hours (DatasetMonitorController::sends() takes only page/per_page).
	// Deliberately a single effect rather than a page-reset effect plus a
	// sendsPage-keyed fetch effect: two effects would fetch page 1 correctly
	// but only after first firing one stale request for the PREVIOUS
	// dataset's page number (the reset's re-render has not landed yet when
	// the fetch effect for the new key first runs), which can race the
	// follow-up page-1 request.
	useEffect(() => {
		if (!datasetKey) {
			return;
		}
		setSendsPage(FIRST_PAGE);
		actions.getSends({
			key: datasetKey,
			page: FIRST_PAGE,
			per_page: SENDS_PER_PAGE,
		});
		// datasetKey is the only thing that should re-trigger this; actions
		// is a stable identity from useDatasetMonitor.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [datasetKey]);

	// Pagination fetches directly rather than through a sendsPage-keyed
	// effect, so a page change can never race the dataset-switch reset above.
	const handleSendsPageChange = useCallback(
		(page) => {
			setSendsPage(page);
			if (datasetKey) {
				actions.getSends({ key: datasetKey, page, per_page: SENDS_PER_PAGE });
			}
		},
		[datasetKey, actions],
	);

	const handleClose = () => {
		dispatch(closeModal());
	};

	const sendsList = sends?.data || sends;
	const totalPages = sendsPagination?.lastPage || FIRST_PAGE;

	return (
		<StandardModal
			title={
				<div>
					<div className="h5 mb-1">{dataset?.label || 'Dataset'}</div>
					<div className="text-muted small">
						{/* The API sends DatasetSource::label() already human-readable
						    ("Open Data", "BIS", "FDNY", "County Clerk", "CityPay",
						    "NYC.gov") - render verbatim. DatasetSource is deliberately
						    a separate enum from App\Consts\DataSource (which means
						    "which DOB filing system produced this record"); routing
						    this through prettifyDataSource (written for that OTHER
						    enum) would reintroduce exactly the conflation DatasetSource
						    exists to prevent - every Open-Data-sourced dataset,
						    including ones with nothing to do with DOB, would read
						    "DOB NOW". */}
						{dataset?.source || null}
					</div>
				</div>
			}
			size="xl"
			className="dataset-detail-modal"
			closeOnOutsideClick
			closeLabel="Close"
			onClose={handleClose}
		>
			<div className="dataset-detail">
				{isLoadingDataset && !dataset ? (
					<div className="text-center py-5">
						<Spinner />
						<p className="text-muted mt-3">Loading dataset statistics...</p>
					</div>
				) : (
					<DatasetStatCards dataset={dataset} hours={hours} />
				)}

				<h5 className="mt-4 mb-3">Recent sends</h5>
				<SendsTable
					sends={Array.isArray(sendsList) ? sendsList : []}
					isLoading={isLoadingSends}
					currentPage={sendsPage}
					totalPages={totalPages}
					onPageChange={handleSendsPageChange}
				/>
			</div>
		</StandardModal>
	);
};

export default DatasetDrawer;
